/**
 * Normalizes pathname for comparisons (trailing slash, empty).
 * @param {string} pathname
 * @returns {string}
 */
export function normalizePathname(pathname) {
  if (!pathname) return '/';
  const t = pathname.replace(/\/+$/, '');
  return t === '' ? '/' : t;
}

/**
 * Virtual match key: progress URLs under a role-prefixed batch tree.
 * Not a real router path — consumed only by isActiveRoute.
 */
export const CODING_PROGRESS_MATCH = '/coding/progress';

/**
 * Virtual match: student assignments, lab dashboard, and IDE (one compact nav row).
 * Not a real router path.
 */
export const STUDENT_ASSIGNMENTS_LABS_MATCH = '/__nav/student/assignments-labs';
export const STUDENT_LAB_RESULTS_MATCH = '/__nav/student/lab-results';
export const STUDENT_PROGRESS_MATCH = '/__nav/student/progress';

/**
 * @param {string} codingHomePath - e.g. '/admin/coding-tracker' or '/faculty/coding-dashboard'
 * @param {string} currentPath
 */
function isCodingHomeActive(codingHomePath, currentPath) {
  const cur = normalizePathname(currentPath);
  const nav = normalizePathname(codingHomePath);
  const m = nav.match(/^(.+)\/(coding-tracker|coding-dashboard)$/);
  if (!m) return false;
  const root = m[1];
  if (cur === nav || cur.startsWith(`${nav}/`)) return true;
  if (root && cur.startsWith(`${root}/batch/`) && !cur.includes('/progress')) return true;
  return false;
}

/**
 * Returns whether a nav item should show as active for the current location.
 *
 * @param {string} path - Nav item route key: real path or {@link CODING_PROGRESS_MATCH}
 * @param {string} currentPath - `location.pathname`
 * @returns {boolean}
 */
export function isActiveRoute(path, currentPath) {
  const cur = normalizePathname(currentPath);
  const nav = normalizePathname(path);

  if (nav === CODING_PROGRESS_MATCH) {
    return (
      cur.includes('/progress') &&
      (cur.startsWith('/admin/batch') || cur.startsWith('/faculty/batch'))
    );
  }

  if (nav === STUDENT_ASSIGNMENTS_LABS_MATCH) {
    return (
      cur.startsWith('/student/assignments') ||
      cur.startsWith('/student/lab-internals') ||
      cur.startsWith('/student/ide/') 
    );
  }

  if (nav === STUDENT_LAB_RESULTS_MATCH) {
    return (
      cur.startsWith('/student/lab-results') ||
      cur.startsWith('/student/results')
    );
  }

  if (nav === STUDENT_PROGRESS_MATCH) {
    return cur.startsWith('/student/progress') || cur.startsWith('/student/coding/progress');
  }

  if (/\/coding-tracker$/.test(nav) || /\/coding-dashboard$/.test(nav)) {
    return isCodingHomeActive(nav, currentPath);
  }

  if (nav.startsWith('/admin/')) {
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav.startsWith('/faculty/')) {
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav.startsWith('/student/')) {
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav.startsWith('/plagiarism-analytics')) {
    return cur === '/plagiarism-analytics' || cur.startsWith('/plagiarism-analytics/');
  }

  if (nav.startsWith('/assignments')) {
    return cur === '/assignments' || cur.startsWith('/assignments/');
  }

  return cur === nav || cur.startsWith(`${nav}/`);
}

/**
 * Active state for a nav item: uses optional `matchPath` when the link `path` differs from match semantics.
 * @param {{ path: string, matchPath?: string }} item
 * @param {string} pathname
 */
export function isNavItemActive(item, pathname) {
  return isActiveRoute(item.matchPath ?? item.path, pathname);
}
